import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { format as fnsFormat, parseISO, isValid } from 'date-fns'
import { Loader2, AlertCircle, CalendarIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { toast } from '@/stores/useToastStore'
import { evaluateFormula } from '@/lib/formulaEvaluator'
import type {
  DagColumnMeta,
  DropdownOptionRead,
  ParentLineItemInfo,
} from '@/types/dag'
import { Calendar } from '@/components/ui/calendar'
import { SkuSearchCell } from './SkuSearchCell'
import { HierarchyDropdownCell } from './HierarchyDropdownCell'
import {
  HIERARCHY_COLUMN_IDS,
  HIERARCHY_LEVEL_MAP,
} from './customColumnRegistry'
import type { HierarchyForDagData } from '@/hooks/useHierarchyForDag'
import type { DagRowRead } from '@/types/dag'
import { getHierarchyOptionsForLevel } from './hierarchyDropdownUtils'

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

export function formatDagValue(value: unknown, format: string): string {
  if (value === null || value === undefined || value === '') return '-'

  const num = typeof value === 'number' ? value : parseFloat(String(value))

  switch (format) {
    case 'currency':
      if (isNaN(num)) return '-'
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case 'percent':
      if (isNaN(num)) return '-'
      return `${(num * 100).toFixed(1)}%`
    case 'number':
      if (isNaN(num)) return '-'
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case 'integer':
      if (isNaN(num)) return '-'
      return Math.round(num).toLocaleString('en-US')
    case 'date': {
      try {
        const d = parseISO(String(value))
        return isValid(d) ? fnsFormat(d, 'MM/dd/yyyy') : '-'
      } catch {
        return '-'
      }
    }
    case 'boolean':
      return value === true || value === 'true' ? 'Yes' : 'No'
    default:
      return String(value)
  }
}

export function parseDagInput(raw: string, format: string): unknown {
  if (raw === '' || raw === '-') return null
  switch (format) {
    case 'percent': {
      const cleaned = raw.replace(/[$,%\s]/g, '')
      const num = parseFloat(cleaned)
      return isNaN(num) ? raw : num / 100
    }
    case 'currency':
    case 'number': {
      const cleaned = raw.replace(/[$,%\s]/g, '')
      const num = parseFloat(cleaned)
      return isNaN(num) ? raw : num
    }
    case 'integer': {
      const cleaned = raw.replace(/[,\s]/g, '')
      const num = parseInt(cleaned, 10)
      return isNaN(num) ? raw : num
    }
    case 'date':
      return raw
    default:
      return raw
  }
}

// ---------------------------------------------------------------------------
// DagDropdownCell
// ---------------------------------------------------------------------------

export interface DagDropdownCellProps {
  value: unknown
  options: DropdownOptionRead[]
  extensible?: boolean
  onSave: (value: unknown) => void
  onCancel: () => void
  onTab?: (shiftKey: boolean) => void
}

export function DagDropdownCell({
  value,
  options,
  extensible,
  onSave,
  onCancel,
  onTab,
}: DagDropdownCellProps) {
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [customInput, setCustomInput] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })
  const inputRef = useRef<HTMLInputElement>(null)
  const customInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const filtered = search
    ? options.filter(
        o =>
          o.value.toLowerCase().includes(search.toLowerCase()) ||
          (o.display_label?.toLowerCase().includes(search.toLowerCase()) ??
            false)
      )
    : options

  // Total items count including the "Other" option
  const totalItems = filtered.length + (extensible && !search ? 1 : 0)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  useEffect(() => {
    if (customInput) {
      customInputRef.current?.focus()
    }
  }, [customInput])

  useEffect(() => {
    if (!containerRef.current) return
    const update = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownHeight =
        dropdownRef.current?.getBoundingClientRect().height || 200
      const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight
      setPosition({
        top: showAbove ? rect.top - dropdownHeight : rect.bottom,
        left: rect.left,
        width: Math.max(rect.width, 220),
      })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [search, customInput])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        const highlighted = filtered[highlightedIndex]
        const copyText = highlighted.display_label || highlighted.value
        navigator.clipboard.writeText(copyText).catch(() => {})
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => (totalItems ? (prev + 1) % totalItems : -1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev =>
          totalItems ? (prev - 1 + totalItems) % totalItems : -1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          onSave(filtered[highlightedIndex].value)
        } else if (
          extensible &&
          !search &&
          highlightedIndex === filtered.length
        ) {
          setCustomInput(true)
        }
        break
      case 'Tab':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          onSave(filtered[highlightedIndex].value)
        } else {
          onCancel()
        }
        onTab?.(e.shiftKey)
        break
      case 'Escape':
        e.preventDefault()
        onCancel()
        break
    }
  }

  const handleCustomKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      e.preventDefault()
      if (customValue.trim()) onSave(customValue.trim())
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setCustomInput(false)
      setCustomValue('')
      setTimeout(() => inputRef.current?.focus(), 0)
    } else if (e.key === 'Tab') {
      e.preventDefault()
      if (customValue.trim()) {
        onSave(customValue.trim())
      } else {
        onCancel()
      }
      onTab?.(e.shiftKey)
    }
  }

  const handleBlur = (e: React.FocusEvent) => {
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) return
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        onCancel()
      }
    }, 150)
  }

  const handleSearchPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text').trim()
    if (!pastedText) return
    e.preventDefault()

    const lower = pastedText.toLowerCase()
    const match = options.find(
      o =>
        o.value.toLowerCase() === lower ||
        (o.display_label?.toLowerCase() ?? '') === lower
    )

    if (match) {
      onSave(match.value)
    } else if (extensible) {
      onSave(pastedText)
    } else {
      toast.error(`"${pastedText}" is not a valid option for this field`)
    }
  }

  // Custom text input mode
  if (customInput) {
    return (
      <div ref={containerRef}>
        <Input
          ref={customInputRef}
          value={customValue}
          onChange={e => setCustomValue(e.target.value)}
          onKeyDown={handleCustomKeyDown}
          onBlur={() => {
            if (customValue.trim()) {
              onSave(customValue.trim())
            } else {
              onCancel()
            }
          }}
          placeholder="Type custom value..."
          className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
    )
  }

  return (
    <div ref={containerRef}>
      <Input
        ref={inputRef}
        value={search}
        onChange={e => {
          setSearch(e.target.value)
          setHighlightedIndex(-1)
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onPaste={handleSearchPaste}
        placeholder={String(value ?? 'Select...')}
        className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
      />
      {createPortal(
        <div
          ref={dropdownRef}
          className="dag-dropdown-portal fixed z-[60] max-h-[200px] overflow-auto rounded border border-[#D6DAE1] bg-white shadow-md"
          style={{
            top: position.top,
            left: position.left,
            minWidth: position.width,
          }}
        >
          {filtered.length === 0 && !extensible ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No options found
            </div>
          ) : (
            <>
              {filtered.map((opt, idx) => (
                <div
                  key={opt.value}
                  className={`cursor-pointer px-2 py-1.5 text-sm hover:bg-muted ${
                    highlightedIndex === idx ? 'bg-muted' : ''
                  }`}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onSave(opt.value)}
                >
                  {opt.display_label || opt.value}
                </div>
              ))}
              {extensible && !search && (
                <div
                  className={`cursor-pointer px-2 py-1.5 text-sm italic text-muted-foreground hover:bg-muted ${
                    highlightedIndex === filtered.length ? 'bg-muted' : ''
                  }`}
                  onMouseEnter={() => setHighlightedIndex(filtered.length)}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => setCustomInput(true)}
                >
                  Other...
                </div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DagDateCell — inline date picker
// ---------------------------------------------------------------------------

interface DagDateCellProps {
  value: unknown
  onSave: (value: unknown) => void
  onCancel: () => void
  onTab?: (shiftKey: boolean) => void
}

function DagDateCell({ value, onSave, onCancel, onTab }: DagDateCellProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  const selectedDate =
    value != null && String(value) !== ''
      ? (() => {
          try {
            const d = parseISO(String(value))
            return isValid(d) ? d : undefined
          } catch {
            return undefined
          }
        })()
      : undefined

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const calendarHeight = 330
      const spaceBelow = window.innerHeight - rect.bottom
      const showAbove = spaceBelow < calendarHeight && rect.top > calendarHeight
      setPosition({
        top: showAbove ? rect.top - calendarHeight : rect.bottom,
        left: rect.left,
      })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      onCancel()
      onTab?.(e.shiftKey)
    }
  }

  const handleBlur = (e: React.FocusEvent) => {
    if (calendarRef.current?.contains(e.relatedTarget as Node)) return
    setTimeout(() => {
      if (!calendarRef.current?.contains(document.activeElement)) {
        onCancel()
      }
    }, 150)
  }

  const displayText = selectedDate
    ? fnsFormat(selectedDate, 'MM/dd/yyyy')
    : 'Select date'

  return (
    <div ref={containerRef}>
      <div
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="flex h-8 items-center gap-1.5 border-0 border-b-2 border-blue-500 rounded-none px-1 text-sm focus:outline-none"
      >
        <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className={selectedDate ? '' : 'text-muted-foreground'}>
          {displayText}
        </span>
      </div>
      {createPortal(
        <div
          ref={calendarRef}
          className="dag-dropdown-portal fixed z-[60] rounded border border-[#D6DAE1] bg-white shadow-md p-0"
          style={{ top: position.top, left: position.left }}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={date => {
              if (date) {
                onSave(fnsFormat(date, 'yyyy-MM-dd'))
              } else {
                onSave(null)
              }
            }}
          />
          <div className="border-t border-[#D6DAE1] px-3 py-2">
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground cursor-pointer"
              onMouseDown={e => e.preventDefault()}
              onClick={() => onSave(null)}
            >
              Clear
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editable inline cell (text / number)
// ---------------------------------------------------------------------------

interface InlineEditProps {
  value: unknown
  format: string
  minValue?: number | null
  maxValue?: number | null
  onSave: (value: unknown) => void
  onCancel: () => void
  onTab?: (shiftKey: boolean) => void
}

function InlineEditCell({
  value,
  format,
  minValue,
  maxValue,
  onSave,
  onCancel,
  onTab,
}: InlineEditProps) {
  const [editValue, setEditValue] = useState(() => {
    if (value == null) return ''
    const num = typeof value === 'number' ? value : parseFloat(String(value))
    if (format === 'percent') {
      if (!isNaN(num)) return String(parseFloat((num * 100).toFixed(2)))
    }
    if ((format === 'currency' || format === 'number') && !isNaN(num)) {
      return String(parseFloat(num.toFixed(2)))
    }
    return String(value)
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const committedRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const commit = useCallback(
    (opts?: { tab?: boolean; shiftKey?: boolean }) => {
      if (committedRef.current) return
      committedRef.current = true
      const parsed = parseDagInput(editValue, format)

      // Range validation for numeric values
      if (typeof parsed === 'number' && !isNaN(parsed)) {
        if (minValue != null && parsed < minValue) {
          toast.error(
            `Value must be at least ${format === 'percent' ? `${minValue * 100}%` : minValue}`
          )
          committedRef.current = false
          return
        }
        if (maxValue != null && parsed > maxValue) {
          toast.error(
            `Value must be at most ${format === 'percent' ? `${maxValue * 100}%` : maxValue}`
          )
          committedRef.current = false
          return
        }
      }

      if (parsed !== value) {
        onSave(parsed)
      } else {
        onCancel()
      }
      if (opts?.tab) onTab?.(opts.shiftKey ?? false)
    },
    [editValue, format, minValue, maxValue, value, onSave, onCancel, onTab]
  )

  const isNumeric = ['currency', 'percent', 'number', 'integer'].includes(
    format
  )

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode={
        isNumeric ? (format === 'integer' ? 'numeric' : 'decimal') : undefined
      }
      value={editValue}
      onChange={e => {
        const val = e.target.value
        if (format === 'integer') {
          // Only digits with optional leading minus
          if (val !== '' && val !== '-' && !/^-?\d+$/.test(val)) return
        } else if (isNumeric) {
          // currency, percent, number: digits, optional minus, optional decimal up to 2 places
          if (val !== '' && val !== '-' && !/^-?\d*\.?\d{0,2}$/.test(val))
            return
        }
        setEditValue(val)
      }}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Tab') {
          e.preventDefault()
          commit({ tab: true, shiftKey: e.shiftKey })
        } else if (e.key === 'Escape') {
          e.preventDefault()
          committedRef.current = true
          onCancel()
        }
      }}
      onBlur={() => commit()}
      className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
    />
  )
}

// ---------------------------------------------------------------------------
// Conditional editability helper
// ---------------------------------------------------------------------------

export function isCellEditable(
  meta: DagColumnMeta,
  cells: Record<string, unknown>
): boolean {
  // num_stores is manually editable when no store model is assigned
  if (meta.id === 'num_stores' && !cells['store_model_id']) {
    return true
  }
  // Hierarchy columns are always editable (frontend manages cascading)
  if (HIERARCHY_COLUMN_IDS.has(meta.id)) return true
  if (!meta.editable) return false
  if (meta.editable_when === 'conditional' && meta.editable_condition) {
    return !!evaluateFormula(meta.editable_condition, cells)
  }
  if (meta.editable_when === 'dummy_only') {
    const sku = cells['plan_sku_num']
    return !sku || !!cells['is_new_sku']
  }
  return true
}

// ---------------------------------------------------------------------------
// DagCellRenderer — main component
// ---------------------------------------------------------------------------

export interface DagCellRendererProps {
  data?: {
    id: string
    isOptionRow?: boolean
    cells?: Record<string, unknown>
    parent_line_item?: ParentLineItemInfo | null
  }
  colDef?: {
    colId?: string
  }
  columnMeta: DagColumnMeta
  dropdownOptions?: DropdownOptionRead[]
  context?: {
    onCellEdit: (
      rowId: string,
      columnId: string,
      value: unknown,
      rowVersion: number
    ) => void
    onMultiCellEdit?: (
      rowId: string,
      cells: Array<{ columnId: string; value: unknown }>,
      rowVersion: number
    ) => void
    onBulkCellEdit?: (
      selectedRows: DagRowRead[],
      columnId: string,
      value: unknown
    ) => void
    onBulkMultiCellEdit?: (
      selectedRows: DagRowRead[],
      cells: Array<{ columnId: string; value: unknown }>
    ) => void
    getBulkSelectedRows?: (rowId: string) => DagRowRead[] | null
    getRowVersion: (rowId: string) => number
    navigateTab?: (
      rowId: string,
      colId: string,
      direction: 'forward' | 'backward'
    ) => void
    isReadOnly?: boolean
    dropdownMap?: Record<string, DropdownOptionRead[]>
    hierarchyData?: HierarchyForDagData | null
  }
}

export function DagCellRenderer({
  data,
  columnMeta,
  dropdownOptions,
  context,
}: DagCellRendererProps) {
  const computing = useDagTableStore(state =>
    state.computingCells.has(`${data?.id}:${columnMeta.id}`)
  )
  const focusedCell = useDagTableStore(s => s.focusedCell)
  const editingCell = useDagTableStore(s => s.editingCell)
  const setFocusedCell = useDagTableStore(s => s.setFocusedCell)
  const setEditingCell = useDagTableStore(s => s.setEditingCell)
  const clearFocus = useDagTableStore(s => s.clearFocus)
  const clearEditing = useDagTableStore(s => s.clearEditing)

  const rowId = data?.id
  const colId = columnMeta.id

  const isFocused = focusedCell?.rowId === rowId && focusedCell?.colId === colId
  const isEditing = editingCell?.rowId === rowId && editingCell?.colId === colId

  const focusRef = useRef<HTMLDivElement>(null)

  // Auto-focus the wrapper div when this cell becomes focused (but not editing)
  useEffect(() => {
    if (isFocused && !isEditing && !computing && focusRef.current) {
      focusRef.current.focus()
    }
  }, [isFocused, isEditing, computing])

  // Use a ref so handleSave always reads the latest context without
  // being in its dependency array (avoids columnDefs regeneration).
  const contextRef = useRef(context)
  contextRef.current = context

  const handleSave = useCallback(
    (value: unknown) => {
      if (!rowId) return
      clearEditing()
      const ctx = contextRef.current
      if (ctx) {
        const selectedRows = ctx.getBulkSelectedRows?.(rowId)
        if (selectedRows && ctx.onBulkCellEdit) {
          ctx.onBulkCellEdit(selectedRows, columnMeta.id, value)
          return
        }
        ctx.onCellEdit(rowId, columnMeta.id, value, ctx.getRowVersion(rowId))
      }
    },
    [rowId, columnMeta.id, clearEditing]
  )

  const handleCancel = useCallback(() => {
    clearEditing()
  }, [clearEditing])

  const handleTab = useCallback(
    (shiftKey: boolean) => {
      if (!rowId) return
      contextRef.current?.navigateTab?.(
        rowId,
        colId,
        shiftKey ? 'backward' : 'forward'
      )
    },
    [rowId, colId]
  )

  const handleCellClick = useCallback(() => {
    if (rowId) setFocusedCell(rowId, colId)
  }, [rowId, colId, setFocusedCell])

  const handleDisplayKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!rowId) return
      if (e.key === 'Tab') {
        e.preventDefault()
        contextRef.current?.navigateTab?.(
          rowId,
          colId,
          e.shiftKey ? 'backward' : 'forward'
        )
      } else if (e.key === 'Enter') {
        const ctxReadOnly = contextRef.current?.isReadOnly ?? false
        const editable =
          !ctxReadOnly && isCellEditable(columnMeta, data?.cells ?? {})
        if (editable) {
          e.preventDefault()
          setEditingCell(rowId, colId)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        clearFocus()
      }
    },
    [rowId, colId, columnMeta, data?.cells, setEditingCell, clearFocus]
  )

  const row = data
  if (!row || row.isOptionRow) return null

  let cellValue = row.cells?.[columnMeta.id]

  if (
    columnMeta.kind === 'derived_local' &&
    columnMeta.expr &&
    (cellValue === null || cellValue === undefined)
  ) {
    cellValue = evaluateFormula(columnMeta.expr, row.cells ?? {})
  }

  const isReadOnly = context?.isReadOnly ?? false
  const isEditable = !isReadOnly && isCellEditable(columnMeta, row.cells ?? {})

  // Computing spinner
  if (computing) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // like_sku_number — render SkuSearchCell without "New SKU" option
  if (columnMeta.id === 'like_sku_number' && isEditable) {
    return (
      <div
        ref={focusRef}
        tabIndex={isFocused && !isEditing ? 0 : -1}
        onClick={handleCellClick}
        onDoubleClick={() => rowId && setEditingCell(rowId, colId)}
        onKeyDown={isFocused && !isEditing ? handleDisplayKeyDown : undefined}
        className={`w-full ${isFocused && !isEditing ? 'dag-cell-focused' : ''}`}
      >
        <SkuSearchCell
          value={cellValue != null ? String(cellValue) : null}
          onSave={skuData => handleSave(skuData.skuNumber)}
          isEditing={isEditing}
          onCancelEdit={handleCancel}
          allowNewSku={false}
          onTab={handleTab}
        />
      </div>
    )
  }

  // Resolve dropdown options once — used by both edit and display modes.
  const liveOptions = columnMeta.dropdown_category
    ? (contextRef.current?.dropdownMap?.[columnMeta.dropdown_category] ??
      dropdownOptions)
    : dropdownOptions

  // Editing mode
  if (isEditing && isEditable) {
    // Hierarchy columns (department → class → subclass) — cascading dropdowns
    if (
      HIERARCHY_COLUMN_IDS.has(columnMeta.id) &&
      contextRef.current?.hierarchyData
    ) {
      return (
        <HierarchyDropdownCell
          value={cellValue}
          hierarchyLevel={HIERARCHY_LEVEL_MAP[columnMeta.id]}
          rowCells={row.cells ?? {}}
          hierarchyData={contextRef.current.hierarchyData}
          onSaveWithCascade={updates => {
            if (!rowId) return
            clearEditing()
            const ctx = contextRef.current
            const selectedRows = ctx?.getBulkSelectedRows?.(rowId)
            if (selectedRows && ctx?.onBulkMultiCellEdit && ctx.hierarchyData) {
              const selectedValue = updates.find(
                update => update.columnId === columnMeta.id
              )?.value

              if (selectedValue != null) {
                const invalidRows = selectedRows.filter(selectedRow => {
                  const options = getHierarchyOptionsForLevel(
                    HIERARCHY_LEVEL_MAP[columnMeta.id],
                    selectedRow.cells ?? {},
                    ctx.hierarchyData as HierarchyForDagData
                  )

                  return !options.some(option => option.value === selectedValue)
                })

                if (invalidRows.length > 0) {
                  toast.error(
                    `Selected value is not valid for ${invalidRows.length} selected ${invalidRows.length === 1 ? 'row' : 'rows'}`
                  )
                  return
                }
              }

              ctx.onBulkMultiCellEdit(selectedRows, updates)
              return
            }

            if (ctx?.onMultiCellEdit) {
              ctx.onMultiCellEdit(rowId, updates, ctx.getRowVersion(rowId))
            }
          }}
          onCancel={handleCancel}
          onTab={handleTab}
        />
      )
    }

    if (columnMeta.dropdown_category && liveOptions) {
      return (
        <DagDropdownCell
          value={cellValue}
          options={liveOptions}
          extensible={columnMeta.dropdown_mode === 'EXTENSIBLE'}
          onSave={handleSave}
          onCancel={handleCancel}
          onTab={handleTab}
        />
      )
    }
    if (columnMeta.format === 'boolean') {
      const boolOptions: DropdownOptionRead[] = [
        { value: 'true', display_label: 'Yes' },
        { value: 'false', display_label: 'No' },
      ]
      return (
        <DagDropdownCell
          value={cellValue === true || cellValue === 'true' ? 'true' : 'false'}
          options={boolOptions}
          onSave={v => handleSave(v === 'true')}
          onCancel={handleCancel}
          onTab={handleTab}
        />
      )
    }
    if (columnMeta.format === 'date') {
      return (
        <DagDateCell
          value={cellValue}
          onSave={handleSave}
          onCancel={handleCancel}
          onTab={handleTab}
        />
      )
    }
    return (
      <InlineEditCell
        value={cellValue}
        format={columnMeta.format}
        minValue={columnMeta.min_value}
        maxValue={columnMeta.max_value}
        onSave={handleSave}
        onCancel={handleCancel}
        onTab={handleTab}
      />
    )
  }

  // Special rendering for sku_level column: show warning for unassigned children
  if (
    columnMeta.id === 'sku_level' &&
    cellValue === 'Child' &&
    !row.parent_line_item
  ) {
    return (
      <TooltipProvider>
        <div
          ref={focusRef}
          tabIndex={isFocused ? 0 : -1}
          onClick={handleCellClick}
          onDoubleClick={
            isEditable ? () => rowId && setEditingCell(rowId, colId) : undefined
          }
          onKeyDown={isFocused ? handleDisplayKeyDown : undefined}
          className={`min-h-[2rem] flex items-center gap-1.5 px-1 w-full overflow-hidden ${
            isEditable ? 'cursor-pointer' : ''
          } ${isFocused ? 'dag-cell-focused' : ''}`}
        >
          <span className="truncate">Child</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertCircle className="h-4 w-4 shrink-0 text-yellow-500 cursor-pointer" />
            </TooltipTrigger>
            <TooltipContent className="bg-[#BB1A28] text-white border-[#BB1A28]">
              <p>No Parent SKU Assigned</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    )
  }

  let formatted: string
  if (
    columnMeta.dropdown_category &&
    liveOptions &&
    cellValue != null &&
    cellValue !== ''
  ) {
    const val = String(cellValue)
    const match = liveOptions.find(o => o.value === val)
    formatted =
      match?.display_label || formatDagValue(cellValue, columnMeta.format)
  } else {
    formatted = formatDagValue(cellValue, columnMeta.format)
  }

  return (
    <div
      ref={focusRef}
      tabIndex={isFocused ? 0 : -1}
      onClick={handleCellClick}
      onDoubleClick={
        isEditable ? () => rowId && setEditingCell(rowId, colId) : undefined
      }
      onKeyDown={isFocused ? handleDisplayKeyDown : undefined}
      className={`min-h-[2rem] flex items-center px-1 w-full overflow-hidden ${
        isEditable ? 'cursor-pointer' : ''
      } ${isFocused ? 'dag-cell-focused' : ''}`}
    >
      <span className="truncate">{formatted}</span>
    </div>
  )
}
